import {
  Popover,
  PopoverBackdrop,
  PopoverButton,
  PopoverPanel,
} from "@headlessui/react";
import { Plus, X } from "lucide-react";
import { FacetChartTypeToolbarIcon } from "./facet-chart-toolbar-icons";
import { CHART_TYPE_PICKER_OPTIONS } from "./facet-chart-types";

export interface ChartTypePickerPopoverProps {
  onSelectChartType: (chartTypeId: string) => void;
  canOpenDerivedViews: boolean;
  showChartIcons: boolean;
  /** Segment-style classes from the toolbar (matches JSON / Table / Chart toggles). */
  segmentButtonClassName: string;
}

/** Chart+ control: Headless UI popover + chart type list. */
export default function ChartTypePickerPopover({
  onSelectChartType,
  canOpenDerivedViews,
  showChartIcons,
  segmentButtonClassName,
}: ChartTypePickerPopoverProps) {
  /**
   * Inside the lime strip, do not reuse `segmentButtonClassName` — it includes `py-2` / min-heights
   * that fight Headless UI and blow past the gray segment track. Use a flat h-7 row + transparent bg.
   */
  const pillStripButtonClass = [
    "relative inline-flex h-7 max-h-7 min-h-[28px] shrink-0 items-center gap-1 whitespace-nowrap rounded-full border-0 bg-transparent px-1.5 py-0 text-[12px] font-semibold outline-none",
    canOpenDerivedViews
      ? "text-slate-900"
      : "cursor-not-allowed text-slate-400 opacity-50",
  ].join(" ");
  const buttonClassName = showChartIcons
    ? pillStripButtonClass
    : segmentButtonClassName;
  const buttonStyle = showChartIcons
    ? {
        height: 28,
        minHeight: 28,
        maxHeight: 28,
        paddingTop: 0,
        paddingBottom: 0,
      }
    : undefined;

  return (
    <Popover
      className={
        showChartIcons
          ? "relative inline-flex h-7 max-h-7 min-h-[28px] items-center"
          : "relative inline-flex"
      }
    >
      <PopoverButton
        type="button"
        className={buttonClassName}
        style={buttonStyle}
        disabled={!canOpenDerivedViews}
        aria-label="Chart options"
      >
        {showChartIcons ? (
          <>
            <span className="hidden sm:inline">Chart</span>
            <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-white text-slate-900 shadow-sm">
              <Plus size={11} strokeWidth={2.5} aria-hidden />
            </span>
          </>
        ) : (
          <>
            <span className="hidden sm:inline text-indigo-800">Chart</span>
            <span className="flex h-4 w-4 items-center justify-center rounded-lg bg-indigo-600 text-white shadow-sm">
              <Plus size={12} strokeWidth={2} aria-hidden />
            </span>
          </>
        )}
      </PopoverButton>
      <PopoverBackdrop
        transition
        className="fixed inset-0 z-[400] bg-slate-900/30 transition data-[closed]:opacity-0"
      />
      <PopoverPanel
        portal
        anchor="bottom end"
        transition
        modal
        className="z-[401] mt-2 w-[min(calc(100vw-24px),280px)] outline-none transition duration-200 ease-out data-[closed]:scale-95 data-[closed]:opacity-0"
      >
        {({ close }) => (
          <section
            className="rounded-xl border border-slate-200 bg-white p-4 shadow-xl"
            aria-labelledby="chart-type-popover-title"
            aria-describedby="chart-type-popover-desc"
          >
            <header className="flex items-start justify-between gap-2 border-b border-slate-100 pb-3">
              <div>
                <h2
                  id="chart-type-popover-title"
                  className="text-[15px] font-semibold text-slate-900"
                >
                  Choose chart type
                </h2>
                <p
                  id="chart-type-popover-desc"
                  className="mt-0.5 text-[13px] text-slate-500"
                >
                  Select a type, then configure data in JSON.
                </p>
              </div>
              <button
                type="button"
                onClick={() => close()}
                className="shrink-0 rounded-md p-1 text-slate-500 hover:bg-slate-100"
                aria-label="Close chart type picker"
              >
                <X size={18} aria-hidden />
              </button>
            </header>
            <ul className="mt-3 flex list-none flex-col gap-1.5 p-0">
              {CHART_TYPE_PICKER_OPTIONS.map((opt) => (
                <li key={opt.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onSelectChartType(opt.id);
                      close();
                    }}
                    className="flex w-full items-center gap-2.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-left text-[13px] font-semibold text-slate-800 transition hover:border-indigo-300 hover:bg-indigo-50"
                  >
                    <span
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-white text-slate-700 shadow-sm ring-1 ring-slate-200/80"
                      aria-hidden
                    >
                      <FacetChartTypeToolbarIcon
                        chartTypeId={opt.id}
                        className="shrink-0 text-green-500 stroke-green-500"
                      />
                    </span>
                    <span className="min-w-0">{opt.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
      </PopoverPanel>
    </Popover>
  );
}
